import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useAuth } from "./AuthContext";
import { useWebSocket } from "./WebSocketContext";
import { notify } from "../utils/notification";
import { WhatsAppCallAudio, describeMicError } from "../utils/whatsappCallAudio";
import {
  WhatsAppCallVideo,
  callVideoSupported,
  describeCameraError,
} from "../utils/whatsappCallVideo";
import { startRingtone, stopRingtone } from "../utils/ringtone";
import { CallTranscriber, transcriptionSupported } from "../utils/callTranscriber";
import { useCallsApi } from "../api/callsApi";

// O toque para quando quem ligou desliga (whatsapp_call_terminated); este é só
// o teto, caso esse aviso não chegue.
const RING_MS = 60000;

const WhatsAppCallContext = createContext(null);

// Modo silencioso: preferência de cada atendente, guardada só neste navegador.
const QUIET_KEY = "chatjuri:calls:quiet";
function readQuietMode() {
  try {
    return window.localStorage.getItem(QUIET_KEY) === "1";
  } catch {
    return false;
  }
}

// Áudio (?callAudio=) e vídeo (?callVideo=) usam a mesma URL (e o mesmo proxy
// do Nginx) do WebSocket do CRM.
function buildMediaUrl(param, callId) {
  const base = process.env.REACT_APP_WS_URL?.replace(
    /^http(s?):/,
    (_, secure) => (secure ? "wss:" : "ws:"),
  );
  const url = new URL(base);
  url.searchParams.set(param, callId);
  return url.toString();
}

export const WhatsAppCallProvider = ({ children }) => {
  const { messages, sendAction, status } = useWebSocket();
  const { user, accessToken } = useAuth();
  const callsApi = useCallsApi();

  const [available, setAvailable] = useState(false);
  // Opção do ADMIN nas Configurações: transcreve toda ligação atendida pelo CRM,
  // desde o primeiro segundo (o atendente avisa o cliente durante a conversa).
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const transcriptionEnabledRef = useRef(false);
  transcriptionEnabledRef.current = transcriptionEnabled;
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptionFailed, setTranscriptionFailed] = useState(false);
  const transcriberRef = useRef(null);
  const transcriptionStartingRef = useRef(false);
  // Permissões do usuário logado (o backend confere de novo a cada ação).
  const [permissions, setPermissions] = useState({ canMakeCalls: false, canReceiveCalls: false });
  const [calls, setCalls] = useState({});
  // Recebidas não atendidas: ficam na tela até alguém retornar ou dispensar.
  const [missedCalls, setMissedCalls] = useState([]);
  const [activeCallId, setActiveCallId] = useState(null);
  const [pending, setPending] = useState(false);
  const [muted, setMuted] = useState(false);
  const [listenOnly, setListenOnly] = useState(false);
  const [audioLive, setAudioLive] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [quietMode, setQuietMode] = useState(readQuietMode);
  const [cameraOn, setCameraOn] = useState(false);
  const [videoLive, setVideoLive] = useState(false);
  const [remoteVideo, setRemoteVideo] = useState(false);

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const videoElsRef = useRef(null);
  const videoLiveRef = useRef(false);
  videoLiveRef.current = videoLive;
  const wantVideoRef = useRef(false);
  const callsRef = useRef({});
  callsRef.current = calls;
  const ringTimeoutRef = useRef(null);
  const ringingIdsRef = useRef(new Set());
  const terminatedIdsRef = useRef(new Set());
  const lastEventIdRef = useRef(null);
  const activeCallIdRef = useRef(null);
  activeCallIdRef.current = activeCallId;

  const isMine = useCallback(
    (call) => call?.ownerUserId != null && String(call.ownerUserId) === String(user?.id),
    [user?.id],
  );

  const silence = useCallback(() => {
    clearTimeout(ringTimeoutRef.current);
    ringingIdsRef.current.clear();
    setRinging(false);
  }, []);

  // Toca enquanto houver alguma ligação ainda chamando do lado de quem ligou.
  const ring = useCallback(
    (callId) => {
      if (terminatedIdsRef.current.has(callId)) return; // já desligou antes do aviso
      ringingIdsRef.current.add(callId);
      setRinging(true);
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(silence, RING_MS);
    },
    [silence],
  );

  const stopRing = useCallback(
    (callId) => {
      terminatedIdsRef.current.add(callId);
      if (terminatedIdsRef.current.size > 50) {
        terminatedIdsRef.current.delete(terminatedIdsRef.current.values().next().value);
      }
      ringingIdsRef.current.delete(callId);
      if (!ringingIdsRef.current.size) silence();
    },
    [silence],
  );

  // No modo silencioso o aviso aparece normalmente, só não toca.
  useEffect(() => {
    if (ringing && !quietMode) startRingtone();
    else stopRingtone();
  }, [ringing, quietMode]);

  const toggleQuietMode = useCallback(() => {
    setQuietMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(QUIET_KEY, next ? "1" : "0");
      } catch {}
      toast(next ? "Toque das ligações desligado" : "Toque das ligações ligado", {
        icon: next ? "🔕" : "🔔",
      });
      return next;
    });
  }, []);

  useEffect(
    () => () => {
      clearTimeout(ringTimeoutRef.current);
      stopRingtone();
    },
    [],
  );

  const stopAudio = useCallback(() => {
    audioRef.current?.stop();
    audioRef.current = null;
    setAudioLive(false);
    setMuted(false);
    setListenOnly(false);
  }, []);

  // Precisa rodar dentro do clique: o navegador só libera microfone e som assim.
  const prepareAudio = useCallback(async () => {
    const audio = audioRef.current || new WhatsAppCallAudio();
    audioRef.current = audio;
    try {
      await audio.prepare();
    } catch (err) {
      const why = describeMicError(err);
      if (!window.confirm(`Microfone indisponível: ${why}.\n\nContinuar só ouvindo? O cliente ouvirá silêncio.`)) {
        stopAudio();
        return false;
      }
      try {
        await audio.prepare({ listenOnly: true });
      } catch (fallbackError) {
        toast.error(`Não foi possível iniciar o áudio: ${fallbackError.message}`);
        stopAudio();
        return false;
      }
    }
    setListenOnly(!audio.hasMic);
    return true;
  }, [stopAudio]);

  const connectAudio = useCallback(
    (callId) => {
      if (!audioRef.current) return;
      audioRef.current.connect(buildMediaUrl("callAudio", callId), [`jwt.${accessToken}`]);
      setAudioLive(true);
    },
    [accessToken],
  );

  // ------------------------------------------------------------ vídeo

  const ensureVideo = useCallback(() => {
    if (!videoRef.current) {
      const video = new WhatsAppCallVideo();
      video.onRemoteFrame = () => {
        video.onRemoteFrame = null; // basta saber que o vídeo do cliente chegou
        setRemoteVideo(true);
      };
      if (videoElsRef.current) video.attach(videoElsRef.current);
      videoRef.current = video;
    }
    return videoRef.current;
  }, []);

  // O painel entrega o <canvas> do cliente e o <video> da própria câmera.
  const attachVideoElements = useCallback((elements) => {
    videoElsRef.current = elements;
    videoRef.current?.attach(elements || {});
  }, []);

  const connectVideo = useCallback(
    (callId) => {
      ensureVideo().connect(buildMediaUrl("callVideo", callId), [`jwt.${accessToken}`]);
      setVideoLive(true);
    },
    [accessToken, ensureVideo],
  );

  // Precisa rodar dentro do clique, como o microfone.
  const startCamera = useCallback(async () => {
    try {
      await ensureVideo().startCamera();
      setCameraOn(true);
      return true;
    } catch (err) {
      toast.error(`Câmera indisponível: ${describeCameraError(err)}`);
      return false;
    }
  }, [ensureVideo]);

  const stopVideo = useCallback(() => {
    videoRef.current?.stop();
    videoRef.current = null;
    wantVideoRef.current = false;
    setVideoLive(false);
    setCameraOn(false);
    setRemoteVideo(false);
  }, []);

  // ------------------------------------------------------------ transcrição

  // Automática quando a ligação fica ativa; o botão do painel só aparece se
  // ela não conseguiu começar (ex.: áudio reconectado depois de recarregar).
  const startTranscription = useCallback(async ({ auto = false } = {}) => {
    const callId = activeCallIdRef.current;
    if (!callId || transcriberRef.current || transcriptionStartingRef.current) return;
    if (!transcriptionSupported()) {
      if (!auto) toast.error("Este navegador não grava a ligação para transcrição: use Chrome ou Edge");
      return;
    }
    // Sem áudio conectado neste navegador não há o que gravar.
    if (!audioRef.current?.connected) {
      if (!auto) toast.error("O áudio da ligação não está ativo");
      return;
    }

    transcriptionStartingRef.current = true;
    try {
      // Grava desde já; o backend só confirma a permissão. Se recusar, descarta.
      const streams = audioRef.current.recordingStreams();
      if (!streams) return;
      // Retomada depois de recarregar a página: a sessão no backend já tem o
      // começo da ligação, e os trechos novos continuam a partir dali.
      let offsetBase = 0;
      const transcriber = new CallTranscriber({
        streams,
        upload: (chunk) =>
          callsApi.sendTranscriptChunk(callId, { ...chunk, offset: chunk.offset + offsetBase }),
      });
      transcriber.callId = callId;
      transcriber.start();
      try {
        const started = await callsApi.startTranscript(callId);
        offsetBase = Number(started?.offsetBase) || 0;
      } catch (err) {
        transcriber.discard();
        setTranscriptionFailed(true);
        toast.error(`Transcrição não iniciada: ${err.message}`);
        return;
      }
      if (callId !== activeCallIdRef.current) {
        // Acabou enquanto o backend confirmava: o fim da ligação já passou.
        transcriber.discard();
        return;
      }
      transcriberRef.current = transcriber;
      setTranscribing(true);
      setTranscriptionFailed(false);
    } finally {
      transcriptionStartingRef.current = false;
    }
  }, [callsApi]);

  const maybeAutoTranscribe = useCallback(
    (call) => {
      if (
        transcriptionEnabledRef.current &&
        call?.id === activeCallIdRef.current &&
        call.state === "active"
      ) {
        startTranscription({ auto: true });
      }
    },
    [startTranscription],
  );

  // Chamar antes de parar o áudio: os gravadores param na hora e o último
  // trecho é capturado antes de o contexto de áudio fechar.
  const finishTranscription = useCallback(() => {
    setTranscriptionFailed(false);
    const transcriber = transcriberRef.current;
    if (!transcriber) return;
    transcriberRef.current = null;
    setTranscribing(false);
    const stopping = transcriber.stop();

    const toastId = toast.loading("Gerando o resumo da ligação…");
    (async () => {
      try {
        const { failures } = await stopping;
        const result = await callsApi.finishTranscript(transcriber.callId);
        if (result?.saved) {
          toast.success(
            (result.summarized
              ? "Ligação transcrita: resumo nas notas, texto completo em \"Ver transcrição\""
              : "Ligação transcrita: veja em \"Ver transcrição\" no registro da ligação") +
              (failures ? ` · ${failures} trecho(s) não transcrito(s)` : ""),
            { id: toastId, duration: 6000 },
          );
        } else {
          toast(result?.reason || "Nada para transcrever", { id: toastId, icon: "ℹ️" });
        }
      } catch (err) {
        toast.error(`Transcrição da ligação: ${err.message}`, { id: toastId });
      }
    })();
  }, [callsApi]);

  const upsertCall = useCallback((call) => {
    setCalls((prev) => ({ ...prev, [call.id]: { ...prev[call.id], ...call } }));
  }, []);

  const removeCall = useCallback((callId) => {
    setCalls((prev) => {
      const next = { ...prev };
      delete next[callId];
      return next;
    });
  }, []);

  const handleEvent = useCallback(
    (event) => {
      switch (event.type) {
        case "whatsapp_call_status": {
          setAvailable(Boolean(event.available));
          setTranscriptionEnabled(Boolean(event.transcriptionEnabled));
          setPermissions({
            canMakeCalls: Boolean(event.canMakeCalls),
            canReceiveCalls: Boolean(event.canReceiveCalls),
          });
          const next = {};
          (event.calls || []).forEach((call) => {
            next[call.id] = call;
          });
          setCalls(next);
          // Página recarregada no meio da ligação: a ligação continua, falta o áudio.
          const mine = (event.calls || []).find(isMine);
          if (mine && !activeCallIdRef.current) setActiveCallId(mine.id);
          break;
        }
        case "whatsapp_status_change":
          sendAction("call_get_state");
          break;
        case "whatsapp_call_incoming": {
          const call = event.call;
          upsertCall(call);
          const who = call.contactName || call.peerPhone || "número não identificado";
          toast(`Ligação de WhatsApp: ${who}`, { icon: "📞", duration: 8000 });
          notify({ title: "Ligação de WhatsApp", body: who });
          ring(call.id);
          break;
        }
        case "whatsapp_call_started":
          silence();
          upsertCall(event.call);
          setPending(false);
          setActiveCallId(event.call.id);
          // Já agora (o ref só se atualiza no próximo render): a transcrição
          // automática abaixo precisa saber qual é a ligação ativa.
          activeCallIdRef.current = event.call.id;
          connectAudio(event.call.id);
          if (event.call.video || wantVideoRef.current) connectVideo(event.call.id);
          maybeAutoTranscribe(event.call);
          break;
        case "whatsapp_call_state": {
          const call = event.call;
          if (call.state === "ended") {
            removeCall(call.id);
            stopRing(call.id);
            if (call.id === activeCallIdRef.current) {
              setActiveCallId(null);
              finishTranscription();
              stopAudio();
              stopVideo();
              toast("Ligação encerrada", { icon: "📞" });
            }
          } else {
            upsertCall(call);
            // Virou vídeo no meio da ligação (pelo cliente): abre o canal de vídeo.
            if (call.id === activeCallIdRef.current && call.video && !videoLiveRef.current) {
              connectVideo(call.id);
            }
            // Outro atendente atendeu: para de tocar aqui.
            if (call.direction === "incoming" && call.ownerUserId && !isMine(call)) silence();
            // Cliente atendeu (ou a recebida conectou): transcreve desde já.
            maybeAutoTranscribe(call);
          }
          break;
        }
        case "whatsapp_call_missed": {
          const call = event.call;
          setMissedCalls((prev) =>
            [call, ...prev.filter((item) => item.id !== call.id)].slice(0, 5),
          );
          const who = call.contactName || call.peerPhone || "número não identificado";
          // Só chega depois que quem ligou desistiu: sem toque, fica o aviso.
          notify({ title: "Ligação perdida no WhatsApp", body: `${who} — clique para retornar` });
          break;
        }
        case "whatsapp_call_terminated":
          stopRing(event.callId);
          break;
        case "whatsapp_call_error":
          setPending(false);
          toast.error(event.message);
          // Ligação que não existe mais não pode ficar com Atender/Recusar na tela.
          if (event.callId && event.callId !== activeCallIdRef.current) {
            removeCall(event.callId);
          }
          if (!activeCallIdRef.current) {
            stopAudio();
            stopVideo();
          }
          break;
        default:
      }
    },
    [
      connectAudio,
      connectVideo,
      finishTranscription,
      isMine,
      maybeAutoTranscribe,
      removeCall,
      ring,
      sendAction,
      silence,
      stopAudio,
      stopRing,
      stopVideo,
      upsertCall,
    ],
  );

  // Processa todos os eventos novos (não só o último): os estados da ligação
  // chegam em rajada e nenhum pode ser pulado.
  useEffect(() => {
    if (!messages.length) return;
    const fresh = [];
    for (const message of messages) {
      if (message.id === lastEventIdRef.current) break;
      fresh.push(message);
    }
    lastEventIdRef.current = messages[0].id;
    fresh.reverse().forEach(handleEvent);
  }, [messages, handleEvent]);

  useEffect(() => {
    if (status === "conectado ao servidor") sendAction("call_get_state");
  }, [status, sendAction]);

  // Permissão alterada pelo ADMIN passa a valer na tela ao voltar para a aba.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") sendAction("call_get_state");
    };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, [sendAction]);

  useEffect(() => {
    if (!activeCallId) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [activeCallId]);

  useEffect(
    () => () => {
      audioRef.current?.stop();
      videoRef.current?.stop();
    },
    [],
  );

  // video: true faz videochamada (câmera ligada desde o início).
  const startCall = useCallback(
    async (conversationId, { video = false } = {}) => {
      if (pending || activeCallIdRef.current) return;
      if (video && !callVideoSupported()) {
        toast.error("Este navegador não faz videochamada: use Chrome ou Edge atualizado");
        return;
      }
      silence();
      if (!(await prepareAudio())) return;
      // Sem câmera, a videochamada segue recebendo o vídeo do cliente.
      if (video) await startCamera();
      wantVideoRef.current = video;
      setPending(true);
      if (!sendAction("call_start", { conversationId, video })) {
        setPending(false);
        stopAudio();
        stopVideo();
        toast.error("Sem conexão com o servidor");
      }
    },
    [pending, prepareAudio, sendAction, silence, startCamera, stopAudio, stopVideo],
  );

  const answerCall = useCallback(
    async (callId) => {
      if (pending || activeCallIdRef.current) return;
      silence();
      if (!(await prepareAudio())) return;
      const video = Boolean(callsRef.current[callId]?.video) && callVideoSupported();
      if (video) await startCamera();
      wantVideoRef.current = video;
      setPending(true);
      if (!sendAction("call_answer", { callId })) {
        setPending(false);
        stopAudio();
        stopVideo();
      }
    },
    [pending, prepareAudio, sendAction, silence, startCamera, stopAudio, stopVideo],
  );

  // Liga/desliga a câmera do atendente; numa ligação de voz, transforma em vídeo.
  const toggleCamera = useCallback(async () => {
    const callId = activeCallIdRef.current;
    if (!callId) return;
    if (cameraOn) {
      await videoRef.current?.stopCamera();
      setCameraOn(false);
      sendAction("call_video", { callId, op: "stop" });
      return;
    }
    if (!callVideoSupported()) {
      toast.error("Este navegador não faz videochamada: use Chrome ou Edge atualizado");
      return;
    }
    if (!(await startCamera())) return;
    if (!videoLiveRef.current) connectVideo(callId);
    sendAction("call_video", { callId, op: "start" });
  }, [cameraOn, connectVideo, sendAction, startCamera]);

  // O cliente pediu para virar vídeo: aceita (a câmera do atendente é opcional).
  const acceptVideo = useCallback(() => {
    const callId = activeCallIdRef.current;
    if (!callId) return;
    if (!videoLiveRef.current) connectVideo(callId);
    sendAction("call_video", { callId, op: "accept" });
  }, [connectVideo, sendAction]);

  const rejectCall = useCallback(
    (callId) => {
      silence();
      sendAction("call_reject", { callId });
    },
    [sendAction, silence],
  );

  const hangup = useCallback(() => {
    if (activeCallIdRef.current) sendAction("call_hangup", { callId: activeCallIdRef.current });
  }, [sendAction]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    audioRef.current?.setMuted(next);
    sendAction("call_mute", { callId: activeCallIdRef.current, on: next });
  }, [muted, sendAction]);

  const dismissMissed = useCallback(
    (callId) => {
      silence();
      setMissedCalls((prev) => prev.filter((item) => item.id !== callId));
    },
    [silence],
  );

  const returnCall = useCallback(
    async (missed) => {
      if (!missed.conversationId) {
        toast.error("Conversa do número não encontrada; ligue pela conversa do contato");
        return;
      }
      dismissMissed(missed.id);
      await startCall(missed.conversationId);
    },
    [dismissMissed, startCall],
  );

  // Retoma o áudio depois de recarregar a página durante a ligação.
  const resumeAudio = useCallback(async () => {
    const callId = activeCallIdRef.current;
    if (!callId || !(await prepareAudio())) return;
    connectAudio(callId);
    maybeAutoTranscribe(callsRef.current[callId]);
  }, [connectAudio, maybeAutoTranscribe, prepareAudio]);

  const value = useMemo(() => {
    const list = Object.values(calls);
    return {
      available,
      canMakeCalls: permissions.canMakeCalls,
      canReceiveCalls: permissions.canReceiveCalls,
      pending,
      muted,
      listenOnly,
      audioLive,
      busy: pending || Boolean(activeCallId),
      activeCall: activeCallId ? calls[activeCallId] || null : null,
      // Só toca para quem pode atender: ligação recebida ainda sem dono.
      incomingCalls: list.filter((call) => call.direction === "incoming" && !call.ownerUserId),
      missedCalls,
      returnCall,
      dismissMissed,
      startCall,
      answerCall,
      rejectCall,
      hangup,
      toggleMute,
      resumeAudio,
      // Toque: "Silenciar" para só o toque atual; modo silencioso vale até desligar.
      ringing: ringing && !quietMode,
      silenceRinging: silence,
      quietMode,
      toggleQuietMode,
      videoSupported: callVideoSupported(),
      cameraOn,
      videoLive,
      remoteVideo,
      toggleCamera,
      acceptVideo,
      attachVideoElements,
      transcriptionEnabled,
      transcribing,
      transcriptionFailed,
      startTranscription,
    };
  }, [
    startTranscription,
    transcriptionFailed,
    transcribing,
    transcriptionEnabled,
    acceptVideo,
    activeCallId,
    answerCall,
    attachVideoElements,
    audioLive,
    available,
    calls,
    cameraOn,
    dismissMissed,
    hangup,
    listenOnly,
    missedCalls,
    muted,
    pending,
    permissions,
    quietMode,
    rejectCall,
    ringing,
    silence,
    toggleQuietMode,
    remoteVideo,
    resumeAudio,
    returnCall,
    startCall,
    toggleCamera,
    toggleMute,
    videoLive,
  ]);

  return <WhatsAppCallContext.Provider value={value}>{children}</WhatsAppCallContext.Provider>;
};

export const useWhatsAppCall = () => {
  const context = useContext(WhatsAppCallContext);
  if (!context) {
    throw new Error("useWhatsAppCall deve ser usado dentro de WhatsAppCallProvider");
  }
  return context;
};
